import { StreamShapes } from "@sablier/v2-constants";
import { BigNumber, _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { dynamic } from "~/client/extensions";
import type { Translate } from "@sablier/v2-locales";
import type { IMilliseconds, IToken } from "@sablier/v2-types";
import type { IExtensionDependencies } from "~/client/types";

function C(t: Translate, confirm: boolean, sequence = false) {
  if (confirm) {
    if (sequence) {
      return t("machines.general.sequence");
    }
    return t("machines.general.confirm");
  }
  return "";
}

function E(t: Translate, purpose: string | undefined) {
  if (purpose === StreamShapes.linear.id) {
    return `${_.startCase(t("structs.lockupLinear"))} ${t("words.stream")}`;
  }

  if (purpose === StreamShapes.cliff.id) {
    return `${_.startCase(t("structs.lockupLinear"))} (${_.capitalize(
      t("words.cliff"),
    )}) ${t("words.stream")}`;
  }

  if (dynamic.includes(purpose + "")) {
    return `${_.startCase(t("structs.lockupDynamic"))} (${_.startCase(
      t(Stream.findShape(purpose).label),
    )}) ${t("words.stream")}`;
  }

  return _.capitalize(t("words.stream"));
}

export const renounce = {
  title: (t: Translate) => t("machines.renounce.title"),
  confirm: (t: Translate) => ({
    description: t("machines.renounce.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.renounce.fail"),
  }),
  success: (t: Translate, end: IMilliseconds) => ({
    description: t("machines.renounce.success", {
      endDate: _.toDuration(end, "date-full")[0],
    }),
  }),
  send: (t: Translate, confirm = false) => ({
    description: t("machines.renounce.send", { confirm: C(t, confirm) }),
  }),
} as const;

export const createGroup = {
  title: (t: Translate) => t("machines.createGroup.title"),
  confirm: (t: Translate) => ({
    description: t("machines.createGroup.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.createGroup.fail"),
    plugin: t("machines.create.failWithPlugin"),
  }),
  success: (t: Translate, label: string, ids?: string) => ({
    description: `${t("machines.createGroup.success", { label })}${
      !_.isNil(ids) ? t("structs.newStreamIds", { ids }) : ""
    }`,
  }),
  send: (t: Translate, confirm = false, label: string) => ({
    description: t("machines.create.send", {
      created: `${label}. ${C(t, confirm, true)}`,
    }),
  }),
  sign: (t: Translate) => ({
    description: t("machines.create.sign"),
  }),
  prepare: (
    t: Translate,
    purpose: string | undefined,
    dependencies: IExtensionDependencies<"group">,
  ) => {
    const entity = E(t, purpose);

    const count = dependencies.streams?.length || 0;

    const total = _.toNumeralPrice(
      dependencies.streams?.reduce(
        (sum, stream) => sum.plus(new BigNumber(stream.amount || "0")),
        new BigNumber(0),
      ) || "0",
    );

    const items = dependencies.streams
      ?.map(
        (stream) =>
          `${_.toShortAddress(stream.address)} (${
            dependencies.token?.symbol
          } ${_.toNumeralPrice(stream.amount)})`,
      )
      .join(", ");

    const plural = count > 1 ? "s" : "";

    const label = t("machines.createGroup.prepare", {
      count,
      plural,
      entity,
      symbol: dependencies.token?.symbol,
      total,
      items,
    });

    const labelShort = t("machines.createGroup.prepareShort", {
      count,
      plural,
      entity,
      symbol: dependencies.token?.symbol,
      total,
    });

    return { label, labelShort };
  },
} as const;

export const createSingle = {
  title: (t: Translate) => t("machines.createSingle.title"),
  confirm: (t: Translate) => ({
    description: t("machines.createSingle.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.createSingle.fail"),
    plugin: t("machines.create.failWithPlugin"),
  }),
  success: (t: Translate, label: string, id?: string) => ({
    description: `${t("machines.createSingle.success", { label })}${
      !_.isNil(id) ? t("structs.newStreamId", { id }) : ""
    }`,
  }),
  send: (t: Translate, confirm = false, label: string) => ({
    description: t("machines.create.send", {
      created: `${label}. ${C(t, confirm, true)}`,
    }),
  }),
  sign: (t: Translate) => ({
    description: t("machines.create.sign"),
  }),

  prepare: (
    t: Translate,
    purpose: string | undefined,
    dependencies: IExtensionDependencies,
  ) => {
    const entity = E(t, purpose);

    const label = t("machines.createSingle.prepare", {
      entity,
      symbol: dependencies.token?.symbol,
      amount: _.toNumeralPrice(dependencies.amount),
    });

    return { label };
  },
} as const;

export const proxy = {
  title: (t: Translate) => t("machines.proxy.title"),
  fail: (t: Translate) => ({
    description: t("machines.proxy.fail"),
  }),
  success: (t: Translate) => ({
    description: t("machines.proxy.success"),
  }),
  send: (t: Translate) => ({
    description: t("machines.proxy.send"),
  }),
} as const;

export const plugin = {
  title: (t: Translate) => t("machines.plugin.title"),
  fail: (t: Translate) => ({
    description: t("machines.plugin.fail"),
  }),
  success: (t: Translate) => ({
    description: t("machines.plugin.success"),
  }),
  send: (t: Translate) => ({
    description: t("machines.plugin.send"),
  }),
} as const;

export const cancel = {
  title: (t: Translate) => t("machines.cancel.title"),
  confirm: (t: Translate) => ({
    description: t("machines.cancel.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.cancel.fail"),
  }),
  success: (t: Translate) => ({
    description: t("machines.cancel.success"),
  }),
  send: (t: Translate, confirm = false) => ({
    description: t("machines.cancel.send", { confirm: C(t, confirm) }),
  }),
} as const;

export const cancelMultiple = {
  title: (t: Translate) => t("machines.cancelMultiple.title"),
  confirm: (t: Translate) => ({
    description: t("machines.cancelMultiple.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.cancelMultiple.fail"),
  }),
  success: (t: Translate) => ({
    description: t("machines.cancelMultiple.success"),
  }),
  send: (t: Translate, confirm = false) => ({
    description: t("machines.cancelMultiple.send", {
      confirm: C(t, confirm),
    }),
  }),
} as const;

export const transfer = {
  title: (t: Translate) => t("machines.transfer.title"),
  confirm: (t: Translate) => ({
    description: t("machines.transfer.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.transfer.fail"),
  }),
  success: (t: Translate, recipient = "unknown") => ({
    description: t("machines.transfer.success", { recipient }),
  }),
  send: (t: Translate, confirm = false, recipient = "unknown") => ({
    description: t("machines.transfer.send", {
      recipient,
      confirm: C(t, confirm),
    }),
  }),
} as const;

export const withdraw = {
  title: (t: Translate) => t("machines.withdraw.title"),
  confirm: (t: Translate) => ({
    description: t("machines.withdraw.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.withdraw.fail"),
  }),
  success: (t: Translate, recipient = "unknown", amount = "0") => ({
    description: t("machines.withdraw.success", { amount, recipient }),
  }),
  send: (
    t: Translate,
    confirm = false,
    recipient = "unknown",
    amount = "0",
  ) => ({
    description: t("machines.withdraw.send", {
      amount,
      recipient,
      confirm: C(t, confirm),
    }),
  }),
  prepare: (token: IToken, value: BigNumber | string) => ({
    amount: `${token.symbol} ${
      _.toValue({ raw: value, decimals: token.decimals }).humanized
    }`,
  }),
} as const;

export const withdrawMultiple = {
  title: (t: Translate) => t("machines.withdrawMultiple.title"),
  confirm: (t: Translate) => ({
    description: t("machines.withdrawMultiple.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.withdrawMultiple.fail"),
  }),
  success: (t: Translate) => ({
    description: t("machines.withdrawMultiple.success"),
  }),
  send: (t: Translate, confirm = false) => ({
    description: t("machines.withdrawMultiple.send", {
      confirm: C(t, confirm),
    }),
  }),
} as const;

export const empty = {
  title: (t: Translate) => t("machines.empty.title"),
  confirm: (t: Translate) => ({
    description: t("machines.empty.confirm"),
  }),
  fail: (t: Translate) => ({
    description: t("machines.empty.fail"),
  }),
  success: (t: Translate, recipient = "unknown", amount = "0") => ({
    description: t("machines.empty.success", { amount, recipient }),
  }),
  send: (
    t: Translate,
    confirm = false,
    recipient = "unknown",
    amount = "0",
  ) => ({
    description: t("machines.empty.send", {
      amount,
      recipient,
      confirm: C(t, confirm),
    }),
  }),
  prepare: (token: IToken, value: BigNumber | string) => ({
    amount: `${token.symbol} ${
      _.toValue({ raw: value, decimals: token.decimals }).humanized
    }`,
  }),
} as const;
